import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiConfig } from '../config/api';

interface TokenValidatorProps {
  children: React.ReactNode;
}

const TokenValidator: React.FC<TokenValidatorProps> = ({ children }) => {
  const { isAuthenticated, loading, token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    const validateToken = async () => {
      // Skip validation if already loading from AuthContext
      if (loading) {
        return;
      }

      // If no token exists, redirect to login
      if (!token) {
        console.log('No token found, redirecting to login');
        navigate('/login', { 
          state: { from: location },
          replace: true 
        });
        setIsValidating(false);
        return;
      }

      // If user exists but we want to validate token with backend
      if (user && token) {
        try {
          // Check if we're in staging/production (using relative URLs)
          const isStagingOrProduction = typeof window !== 'undefined' && 
            (window.location.hostname.includes('stg-') || 
             window.location.hostname.includes('staging') ||
             (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')));
          
          // For staging/production, ALWAYS use relative URL regardless of apiConfig
          let authUrl: string;
          if (isStagingOrProduction) {
            authUrl = '/api';
            console.log('🌐 [STAGING] Using relative URL for token validation:', authUrl);
          } else {
            // For localhost, use apiConfig or default
            const authBaseUrl = apiConfig.authServiceUrl || apiConfig.baseUrl;
            // Ensure we use port 3001 for auth on localhost
            authUrl = authBaseUrl.includes('3001') 
              ? authBaseUrl 
              : 'http://localhost:3001/api/v1';
          }
          
          console.log('Validating token with backend...', authUrl);
          const response = await fetch(`${authUrl}/auth/me`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            console.log('Token is valid');
            if (!cancelled) {
              setIsValidating(false);
              setValidationError(null);
            }
          } else if (response.status === 401) {
            console.log('Token is invalid or expired');
            // Clear invalid tokens
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            
            // Redirect to login with current location
            if (!cancelled) {
              navigate('/login', { 
                state: { from: location },
                replace: true 
              });
              setIsValidating(false);
            }
          } else {
            console.error('Token validation failed with status:', response.status);
            if (!cancelled) {
              setValidationError('Token validation failed');
              setIsValidating(false);
            }
          }
        } catch (error) {
          console.error('Error validating token:', error);
          if (!cancelled) {
            setValidationError('Network error during token validation');
            setIsValidating(false);
          }
        }
      } else {
        // No user data, redirect to login
        console.log('No user data found, redirecting to login');
        if (!cancelled) {
          navigate('/login', { 
            state: { from: location },
            replace: true 
          });
          setIsValidating(false);
        }
      }
    };

    if (!inFlight) {
      inFlight = true;
      validateToken().finally(() => { inFlight = false; });
    }
    return () => { cancelled = true; };
  }, [token, user, loading, navigate, location]);

  // Show loading while validating
  if (loading || isValidating) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Validating authentication...</p>
      </div>
    );
  }

  // Show error if validation failed
  if (validationError) {
    return (
      <div className="error-container">
        <h2>Authentication Error</h2>
        <p>{validationError}</p>
        <button 
          onClick={() => {
            localStorage.clear();
            navigate('/login', { replace: true });
          }}
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  // If authenticated and token is valid, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // This should not be reached due to redirects above, but just in case
  return null;
};

export default TokenValidator;
